import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, ViewChild, forwardRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ControlTypes, FormKeys } from '@nx-customer-apps/shared/enums';
import { Question, QuestionBase, TypesHTMLHelper, isQuestionConditionMet } from '@nx-customer-apps/shared/utils';
import { FormFieldHighlights } from '@nx-customer-apps/shared/interfaces';
import { ViewHostDirective } from '../../directives/view-host.directive';
import { NgClass } from '@angular/common';
import { MatTooltip } from '@angular/material/tooltip';
import { TextboxComponent } from '../textbox/textbox.component';
import { RadioComponent } from '../radio/radio.component';
import { CheckboxComponent } from '../checkbox/checkbox.component';
import { CheckboxSetComponent } from '../checkbox-set/checkbox-set.component';
import { MultiTextboxComponent } from '../multi-textbox/multi-textbox.component';
import { MultiQuestionboxComponent } from '../multi-questionbox/multi-questionbox.component';
import { CustomAnswerRadioComponent } from '../custom-answer-radio/custom-answer-radio.component';
import { SliderComponent } from '../slider/slider.component';
import { StepperComponent } from '../stepper/stepper.component';
import { DropdownComponent } from '../dropdown/dropdown.component';
import { SearchDropdownComponent } from '../search-dropdown/search-dropdown.component';
import { AddressboxComponent } from '../addressbox/addressbox.component';
import { AdvancedSearchComponent } from '../advanced-search/advanced-search.component';
import { TranslatePipe } from '@ngx-translate/core';
import { AsPipe } from '../../pipes';
import { ComponentsModule } from '@nx-customer-apps/components';

@Component({
    selector: 'vp-question',
    templateUrl: './question.component.html',
    styleUrls: ['./question.component.scss'],
    imports: [
        ReactiveFormsModule,
        NgClass,
        ComponentsModule,
        MatTooltip,
        ViewHostDirective,
        TextboxComponent,
        RadioComponent,
        CheckboxComponent,
        CheckboxSetComponent,
        MultiTextboxComponent,
        MultiQuestionboxComponent,
        CustomAnswerRadioComponent,
        SliderComponent,
        StepperComponent,
        DropdownComponent,
        SearchDropdownComponent,
        AddressboxComponent,
        AdvancedSearchComponent,
        TranslatePipe,
        AsPipe,
        forwardRef(() => QuestionComponent)
    ]
})
export class QuestionComponent implements OnInit {
    @Input() form: FormGroup;
    @Input() question: Question;
    @Output() questionTrigger: EventEmitter<{ key: FormKeys; value: any }> = new EventEmitter();
    @Input() highlights: FormFieldHighlights = {};
    @ViewChild(ViewHostDirective) contentHost: ViewHostDirective;
    readonly CONTROL_TYPES: typeof ControlTypes = ControlTypes;
    readonly TYPES = TypesHTMLHelper;
    public isSubQuestionVisible = false;
    private destroyRef = inject(DestroyRef);

    public trackBySelf(index: number, item: string): string {
        return item;
    }

    public ngOnInit(): void {
        this.initSubQuestionVisibility();
    }

    public ngAfterViewInit() {
        this.setContentHost();
    }

    private setContentHost() {
        if (this.question.content) {
            this.contentHost.viewContainerRef.clear();
            this.contentHost.viewContainerRef.createComponent(this.question.content!);
        }
    }

    private initSubQuestionVisibility(): void {
        const subQuestion = (this.question as QuestionBase<any>).subQuestion;
        if (!subQuestion) {
            return;
        }
        const parentControl = this.form.get((this.question as QuestionBase<any>).key);
        if (!parentControl) {
            return;
        }
        this.updateSubQuestionState(parentControl.value, subQuestion);
        parentControl.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(value => this.updateSubQuestionState(value, subQuestion));
    }

    private updateSubQuestionState(parentValue: unknown, subQuestion: QuestionBase<any>): void {
        this.isSubQuestionVisible = isQuestionConditionMet(parentValue, subQuestion.subQuestionShowWhen);
        const control = this.form.get(subQuestion.key);
        if (!control) {
            return;
        }
        control.setValidators(this.isSubQuestionVisible ? subQuestion.validators : []);
        control.updateValueAndValidity({ emitEvent: false });
    }
}
